import React, {useState} from 'react';
import DateInput from '../../UI/DateInput';
import { X } from 'lucide-react';
import useApi from "../../../utils/useApi";
import {useTaxYear} from "../../../contexts/TaxYearContext";

const ReturnsNeeded = ({
                           years,
                           formData,
                           setFormData,
                           client,
                           baseInputStyles
                       }) => {

    const {taxYear} = useTaxYear();
    const {deleteReturnNeeded} = useApi();

    const formatDateForInput = (dateString) => {
        if (!dateString) return '';

        // Handle different date formats
        let date;

        // Try parsing MM/DD/YYYY format
        if (dateString.includes('/')) {
            const [month, day, year] = dateString.split('/');
            date = new Date(year, month - 1, day);
        }
        // Try parsing ISO format (YYYY-MM-DDTHH:mm:ss.sssZ)
        else if (dateString.includes('T')) {
            date = new Date(dateString);
        }
        // Try parsing YYYY-MM-DD format
        else if (dateString.includes('-')) {
            date = new Date(dateString);
        } else {
            return '';
        }

        // Check if date is valid
        if (isNaN(date.getTime())) {
            return '';
        }

        // Format date as YYYY-MM-DD
        return date.toISOString().split('T')[0];
    };

    const handleAddReturn = (e) => {
        const selectedYear = e.target.value;
        if (!selectedYear) return;

        // Add new return needed
        const newReturn = {
            user_id: client.id,
            year: selectedYear,
            status: 'pending',
            fee: '',
            notes: '',
            filed_date: '',
            initials: ''
        };

        setFormData(prev => ({
            ...prev,
            returns_needed: [...(prev.returns_needed || []), newReturn]
        }));

        // Reset select
        e.target.value = '';
    };

    const handleRemoveReturn = async (index, id) => {
        if(id){
            await deleteReturnNeeded(id);
        }
        setFormData(prev => ({
            ...prev,
            returns_needed: prev.returns_needed.filter((_, i) => i !== index)
        }));
    };

    const handleReturnChange = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            returns_needed: prev.returns_needed.map((return_needed, i) =>
                                                        i === index ? { ...return_needed, [field]: value } : return_needed
            )
        }));
    };

    // Filter out years that are already in returns_needed
    const availableYears = years.filter(year =>
                                            !formData.returns_needed?.some(return_needed =>
                                                                               return_needed.year == year.toString()
                                            )
    );

    return (
        <div className="space-y-4">
            <select
                onChange={handleAddReturn}
                defaultValue=""
                className={baseInputStyles}
            >
                <option value="">Select Year to Add Return Needed</option>
                {availableYears.map(year => (
                    <option key={year} value={year}>{year}</option>
                ))}
            </select>
            <div className={`grid flex grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3  gap-2`}>
                {formData.returns_needed?.map((return_needed, index) => (
                    <div key={index} className="border flex rounded-lg p-4 space-y-4 relative">
                        <button
                            type="button"
                            onClick={() => handleRemoveReturn(index, return_needed?.id)}
                            className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
                        >
                            <X className="w-4 h-4" />
                        </button>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                                    Year
                                </label>
                                <input
                                    type="text"
                                    value={return_needed.year ?? ''}
                                    disabled
                                    className={`${baseInputStyles} bg-gray-100`}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                                    Fee
                                </label>
                                <input
                                    type="number"
                                    value={return_needed.fee ?? ''}
                                    onChange={(e) => handleReturnChange(index, 'fee', e.target.value)}
                                    className={baseInputStyles}
                                />
                            </div>

                            <div className={`w-full flex flex-col col-span-2`}>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                                    Date Filed
                                </label>
                                <input
                                    type="date"
                                    value={formatDateForInput(return_needed.filed_date)}
                                    onChange={(e) => handleReturnChange(index, 'filed_date', e.target.value)}
                                    className={baseInputStyles}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                                    Initials
                                </label>
                                <input
                                    type="text"
                                    value={return_needed.initials ?? ''}
                                    onChange={(e) => handleReturnChange(index, 'initials', e.target.value)}
                                    maxLength={3}
                                    className={baseInputStyles}
                                />
                            </div>

                            <div className="col-span-2">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                                    Notes
                                </label>
                                <textarea
                                    value={return_needed.notes ?? ''}
                                    onChange={(e) => handleReturnChange(index, 'notes', e.target.value)}
                                    rows={3}
                                    className={baseInputStyles}
                                />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ReturnsNeeded;
